"use client";

import { forwardRef, useMemo } from "react";
import { cn } from "../../utils";
import { VARIABLE_REF_TYPE, type ChipListHandle } from "../rich-chip-editor";
import { SuggestionListContainer, useSuggestionListNav } from "../rich-chip-editor/suggestion-list";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";

// The context-variable half of the workflow-template surface: the item shape
// callers already build from a node's upstream schema
// (apps/web/.../context-from-schema.ts), plus the two ways of choosing one —
// the inline `{{` popup and the explicit picker popover.

export type ContextVariableType = "string" | "number" | "boolean" | "array" | "object" | "id";

export interface ContextVariable {
  /** Full path for template insertion, e.g., "trigger.emailData.sender" */
  path: string;
  /** Human-readable label, e.g., "Sender Email" */
  label: string;
  /** Optional description */
  description?: string;
  /** Data type */
  type: ContextVariableType;
  /** Grouping for the picker, e.g., "Trigger" or step name */
  group: string;
}

/** Chip attrs for a context variable — the path is both id and rendered text,
 *  matching what a hand-typed `{{path}}` tag decomposes to. */
export function contextVariableChip(variable: ContextVariable) {
  return { id: variable.path, label: variable.path, refType: VARIABLE_REF_TYPE };
}

/** Case-insensitive path/label filter, capped so the popup stays scannable. */
export function filterContextVariables(
  variables: ContextVariable[],
  query: string,
  limit = 20,
): ContextVariable[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return variables.slice(0, limit);
  return variables
    .filter(
      (item) =>
        item.path.toLowerCase().includes(normalized) ||
        item.label.toLowerCase().includes(normalized),
    )
    .slice(0, limit);
}

// ============================================================================
// Inline `{{` popup
// ============================================================================

export interface ContextVariableListProps {
  items: ContextVariable[];
  command: (attrs: { id: string; label: string; refType: string }) => void;
}

/** The `{{` popup list, sectioned by each variable's source node. */
export const ContextVariableList = forwardRef<ChipListHandle, ContextVariableListProps>(
  ({ items, command }, ref) => {
    const selectItem = (index: number): void => {
      const item = items[index];
      if (item) command(contextVariableChip(item));
    };

    const { selectedIndex, itemRef } = useSuggestionListNav<HTMLButtonElement>(
      ref,
      items.length,
      selectItem,
      items,
    );

    // Group entries by `group` while preserving order, so authors can scan by
    // source node without the list re-ordering under them.
    const groups = useMemo(() => {
      const out: Array<{ name: string; items: Array<{ entry: ContextVariable; index: number }> }> =
        [];
      for (const [index, entry] of items.entries()) {
        const last = out[out.length - 1];
        if (!last || last.name !== entry.group) {
          out.push({ name: entry.group, items: [{ entry, index }] });
        } else {
          last.items.push({ entry, index });
        }
      }
      return out;
    }, [items]);

    return (
      <SuggestionListContainer
        className="variable-chip-popup w-80"
        empty={items.length === 0}
        emptyMessage="No matching variables"
        emptyClassName="text-xs"
      >
        {groups.map((group) => (
          <div key={group.name} className="mb-1 last:mb-0">
            <div className="px-2 pb-0.5 pt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {group.name}
            </div>
            {group.items.map(({ entry, index }) => (
              <button
                key={entry.path}
                ref={itemRef(index)}
                type="button"
                className={cn(
                  "flex w-full flex-col items-start gap-0.5 rounded-sm px-2 py-1 text-left outline-none",
                  "hover:bg-accent hover:text-accent-foreground",
                  index === selectedIndex && "bg-accent text-accent-foreground",
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectItem(index)}
              >
                <span className="font-mono text-[11px] leading-tight">{entry.path}</span>
                <span className="text-[10px] text-muted-foreground">{entry.label}</span>
              </button>
            ))}
          </div>
        ))}
      </SuggestionListContainer>
    );
  },
);

ContextVariableList.displayName = "ContextVariableList";

// ============================================================================
// Explicit picker popover
// ============================================================================

/** Searchable, grouped variable list for the "Context" / "+" popover — the
 *  discoverable counterpart to typing `{{`. */
export function ContextVariablePicker({
  variables,
  onSelect,
}: {
  variables: ContextVariable[];
  onSelect: (variable: ContextVariable) => void;
}) {
  const grouped = useMemo(() => {
    const groups = new Map<string, ContextVariable[]>();
    for (const variable of variables) {
      const existing = groups.get(variable.group) ?? [];
      existing.push(variable);
      groups.set(variable.group, existing);
    }
    return groups;
  }, [variables]);

  return (
    <Command>
      <CommandInput placeholder="Search variables..." />
      <CommandList>
        <CommandEmpty>No variables found.</CommandEmpty>
        {Array.from(grouped.entries()).map(([group, entries]) => (
          <CommandGroup key={group} heading={group}>
            {entries.map((variable) => (
              <CommandItem
                key={variable.path}
                value={`${variable.group} ${variable.label} ${variable.path}`}
                onSelect={() => onSelect(variable)}
                className="flex flex-col items-start gap-1"
              >
                <code className="rounded bg-muted px-1 text-xs text-green-700 dark:text-green-400">
                  {`{{${variable.path}}}`}
                </code>
                <div className="text-xs text-muted-foreground">
                  {variable.description || variable.label}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}
